import { useEffect, useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { Badge, Button, Input, Modal, Select, Textarea, useToast } from '@/components/ui'
import { fileToBase64 } from '../api/templatesApi.ts'
import { useUploadSignedDocument } from '../api/templatesApi.ts'
import type { ContractDocument, SignatureProvider } from '../types.ts'

interface UploadSignedModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  contractId: string
  document: ContractDocument | null
}

const PROVIDER_OPTIONS: { value: SignatureProvider; label: string }[] = [
  { value: 'none', label: 'Manuell / Sonstiges' },
  { value: 'yousign', label: 'Yousign' },
  { value: 'skribble', label: 'Skribble' },
]

export function UploadSignedModal({ open, onOpenChange, contractId, document }: UploadSignedModalProps) {
  const { toast } = useToast()
  const upload = useUploadSignedDocument(contractId)
  const fileInput = useRef<HTMLInputElement | null>(null)
  const [pendingFile, setPendingFile] = useState<{ name: string; base64: string; size: number; mime: string } | null>(null)
  const [provider, setProvider] = useState<SignatureProvider>('yousign')
  const [externalRef, setExternalRef] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setPendingFile(null)
      setProvider((document?.signatureProvider as SignatureProvider) ?? 'yousign')
      setExternalRef(document?.signatureExternalId ?? '')
      setNotes(document?.notes ?? '')
      setError(null)
    }
  }, [open, document])

  async function handleFile(file: File) {
    if (file.size > 15 * 1024 * 1024) {
      setError('Datei zu groß (max 15 MB).')
      return
    }
    try {
      const base64 = await fileToBase64(file)
      setPendingFile({
        name: file.name,
        base64,
        size: file.size,
        mime:
          file.type ||
          (file.name.toLowerCase().endsWith('.pdf')
            ? 'application/pdf'
            : 'application/octet-stream'),
      })
      setError(null)
    } catch (err) {
      setError(`Datei konnte nicht gelesen werden: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  function handleSave() {
    if (!document) return
    if (!pendingFile) {
      setError('Bitte das signierte PDF/DOCX wählen.')
      return
    }
    upload.mutate(
      {
        docId: document.id,
        input: {
          fileContentBase64: pendingFile.base64,
          fileName: pendingFile.name,
          mimeType: pendingFile.mime,
          signatureProvider: provider,
          signatureExternalId: externalRef.trim() || null,
          notes: notes || null,
        },
      },
      {
        onSuccess: () => {
          toast({ title: 'Signiertes Dokument hochgeladen', tone: 'success' })
          onOpenChange(false)
        },
        onError: (err) => setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen'),
      },
    )
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title="Signiertes Dokument hochladen"
      description={
        document
          ? `Ersetzt „${document.fileName}" mit dem signierten PDF/DOCX. Status wechselt auf „Signiert", Datum + Provider werden festgehalten.`
          : ''
      }
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)} disabled={upload.isPending}>
            Abbrechen
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} loading={upload.isPending}>
            Hochladen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">{error}</div>
        ) : null}

        <div className="space-y-2">
          <p className="text-small font-medium text-gray-700">Datei</p>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              ref={fileInput}
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={(e) => {
                const file = e.currentTarget.files?.[0]
                if (file) void handleFile(file)
                e.currentTarget.value = ''
              }}
            />
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Upload className="w-4 h-4" />}
              onClick={() => fileInput.current?.click()}
            >
              Datei wählen
            </Button>
            {pendingFile ? (
              <Badge variant="brand" size="sm">
                {pendingFile.name} · {(pendingFile.size / 1024).toFixed(0)} KB
              </Badge>
            ) : (
              <span className="text-caption text-gray-500">PDF oder DOCX</span>
            )}
          </div>
        </div>

        <Select<SignatureProvider>
          label="Signatur-Anbieter"
          hint="Welcher Anbieter wurde für die Unterschrift verwendet?"
          value={provider}
          onValueChange={setProvider}
          options={PROVIDER_OPTIONS}
        />

        <Input
          label="Anbieter-Referenz"
          hint="Optional — z. B. Yousign-Procedure-ID, für späteres Nachschlagen"
          value={externalRef}
          onChange={(e) => setExternalRef(e.currentTarget.value)}
        />

        <Textarea
          label="Notizen"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />
      </div>
    </Modal>
  )
}
